import { useTranslation } from "react-i18next";
import { FormProvider } from "react-hook-form";
import { useNavigate } from "react-router-dom";

import * as AF from "../form-items/AbstractForm";
import { SupportedLocale } from "@/i18n";
import { Separator } from "../ui/separator";
import { DataTable } from "../ui/datatable";
import { FileIcon, GripVertical, Search } from "lucide-react";
import { Badge } from "../ui/badge";
import { useEffect, useMemo, useRef, useState } from "react";
import { PagingParameters } from "@/api/dtos";
import { Row } from "@tanstack/react-table";
import { SubmittedAuthenticationForm } from "@/lib/types";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { DatatablePagination } from "../ui/datatable-pagination";
import { useGetSubmittedAuthenticationForms, usePricesInsertion } from "@/api/cms";
import { FieldOption } from "@/config/forms/general";
import { FormField, FormControl, HeadlessFormItem } from "../ui/form";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../ui/select";
import { Input } from "../ui/input";
import { downloadPdf } from "@/api/cms";

export interface AuthenticationFormProps {
  language: SupportedLocale;
  instanceId: string;
}

export const AuthenticationForm = ({ language }: AuthenticationFormProps) => {
  const { t } = useTranslation();

  return (
    <AF.Col>
      <p className="text-[#0c111d] leading-[1.5rem] text-lg font-semibold font-['Raleway'] leading-normal">
        {t("cms.authentication.homePage")}
      </p>
      <AF.Text
        name={`${language}.title`}
        label={t("cms.authentication.homePage.pagetitle")}
      />
      <AF.RichText
        name={`${language}.content`}
        label={t("cms.authentication.homePage.textContent")}
      />
      <AF.RichText
        name={`${language}.sidebarTitle`}
        label={t("cms.authentication.homePage.sidebarTitle")}
      />
      <Separator
        orientation="horizontal"
        className="w-full h-[0.5px] bg-gray-300"
      />
      <p className="text-[#0c111d] leading-[1.5rem] text-lg font-semibold font-['Raleway'] leading-normal">
        {t("cms.authentication.legalNotice.title")}
      </p>
      <AF.RichText
        name={`${language}.honorDeclaration`}
        label={t("cms.authentication.endPage.honorDeclaration")}
      />
      <AF.RichText
        name={`${language}.certificationLegalStatement`}
        label={t("cms.authentication.endPage.legalNotice")}
      />
    </AF.Col>
  );
};


export const AuthenticationListForm = (_props: AuthenticationFormProps) => {
  const { t, i18n } = useTranslation();
  const [pagination, setPagination] = useState<PagingParameters>({
    page: 0,
    pageSize: 10,
  });
  const [filters, setFilters] = useState<{ search?: string; artworkType?: string, instanceId: string}>({
    instanceId: _props.instanceId,
    search: '',
    artworkType: 'all',
  });
  const [_totalSize, _setTotalSize] = useState<number>(0);
  const { data: prices } = usePricesInsertion();

  const queryParams = {
    instanceId: filters.instanceId,
    ...pagination,
    ...(filters.search && filters.search !== '' ? { search: filters.search } : {}),
    ...(filters.artworkType && filters.artworkType !== 'all' ? { artworkType: filters.artworkType } : {}),
  };
  const { data: submissionListData } = useGetSubmittedAuthenticationForms(queryParams);

  const columnsData = [
    {
      header: t("cms.authentication.list.table.form-number"),
      size: 10,
      enableSorting: false,
      id: "id",
      accessorKey: "id",
      cell: ({ row }: any) => {
        return row.original.id.split('-')[0];
      }
    },
    {
      header: t("cms.authentication.list.table.reception-date"),
      size: 10,
      enableSorting: false,
      id: "createdAt",
      accessorKey: "createdAt",
      cell: ({ row }: any) => {
        const createdAt = new Date(row.original.createdAt);
        return createdAt.toLocaleDateString();
      }
    },
    {
      header: t("cms.authentication.list.table.sender"),
      size: 10,
      enableSorting: false,
      id: "signatoryName",
      accessorKey: "signatoryName",
    },
    {
      header: t("cms.authentication.list.table.type"),
      size: 10,
      enableSorting: false,
      cell: ({ row }: any) => {
        return (
          <Badge variant="outlined">
            {row.original.price[`name_${currentLanguage}`] ?? row.original.price.name_en}
          </Badge>
        );
      },
    },
    {
      header: t("cms.authentication.list.table.status"),
      size: 10,
      enableSorting: false,
      id: "isPaid",
      accessorKey: "isPaid",
      cell: ({ row }: any) => {
        const isPaid = row.original.isPaid;
        return (
          <Badge variant={isPaid ? "published" : "error"}>
            {isPaid ? t("cms.authentication.list.table.paid") : t("cms.authentication.list.table.unpaid")}
          </Badge>
        );
      },
    },
    {
      header: '',
      size: 10,
      id: 'actions',
      enableSorting: false,
      cell: ({ row }: any) => {
        return (
          <div
            className="w-[60px] h-[40px] flex justify-center items-center bg-gray-100 rounded-md cursor-pointer hover:bg-gray-200"
            style={{ width: `60px`, height: `40px` }}
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              downloadPdf(row.original);
            }}
          >
            <FileIcon className="w-4 h-4 text-gray-500" />
          </div>
        );
      },
    },
  ]

  const form = useForm<{search?: string; artworkType?: string}>({
    resolver: zodResolver(z.object({
      search: z.string().optional(),
      artworkType: z.string().optional(),
    })),
    defaultValues: {
      search: '',
      artworkType: 'all',
    }
  });

  const { handleSubmit, watch } = form;

  const artworkType = watch('artworkType');
  const isInitialMount = useRef(true);

  const currentLanguage = (i18n.language as SupportedLocale) || "en";

  const artworkTypeOptions: FieldOption[] = useMemo(() => {
    const allOption: FieldOption = {
      label: t("cms.authentication.list.artworkType.all"),
      value: "all",
    };

    if (!prices || !Array.isArray(prices)) return [allOption];

    const priceOptions = prices.map((price) => {
      const label = currentLanguage === "fr"
        ? (price.name_fr || price.name_en || "")
        : (price.name_en || "");

      return {
        label,
        value: price.name_en,
      };
    });

    return [allOption, ...priceOptions];
  }, [prices, currentLanguage, t]);

  const navigate = useNavigate();

  const handleRowClick = (row: Row<SubmittedAuthenticationForm>) => {
    navigate(`/cms/artwork-authentication/${row.original.id}`);
  };

  const searchSubmittedForm = async () => {
    const { search, artworkType } = form.getValues();
    setFilters({
      instanceId: _props.instanceId,
      search: search || '',
      artworkType: artworkType || 'all',
    });
    setPagination({
      page: 0,
      pageSize: pagination.pageSize,
    });
    // Le hook React Query se rechargera automatiquement grâce à la queryKey qui inclut les filtres
  }

  // Déclencher automatiquement la recherche quand artworkType change
  useEffect(() => {
    // Éviter de déclencher lors du montage initial
    if (isInitialMount.current) {
      isInitialMount.current = false;
      return;
    }

    // Déclencher la recherche uniquement lors des changements ultérieurs
    if (artworkType !== undefined) {
      handleSubmit(searchSubmittedForm)();
    }
  }, [artworkType]); // eslint-disable-line react-hooks/exhaustive-deps

  return (
    <FormProvider {...form}>
      <div className="flex flex-col gap-4 mt-8">
        <form
          onSubmit={handleSubmit(searchSubmittedForm)}
          className="flex flex-col w-full gap-4"
          id="submitted-form-search"
        >
          <div className="flex flex-row gap-2 items-end">
            <div className="w-[80%]">
              <FormField
                {...form}
                name="search"
                render={({ field }) => (
                  <HeadlessFormItem>
                    <FormControl>
                      <div className="flex flex-col gap-1">
                        <label
                          htmlFor="search"
                          className="flex text-start mb-1.5 text-slate-700 text-sm font-medium font-['Raleway'] leading-normal"
                        >
                          {t("cms.authentication.list.search.label")}
                        </label>
                        <div className="relative">
                          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-5 w-3 text-muted-foreground" />
                          <Input
                            id="search"
                            {...field}
                            className="pl-4"
                          />
                        </div>
                      </div>
                    </FormControl>
                  </HeadlessFormItem>
                )}
              />
            </div>
            <div className="w-[20%]">
              <FormField
                {...form}
                name="artworkType"
                render={({ field }) => (
                  <HeadlessFormItem>
                    <FormControl>
                      <Select
                        value={field.value ?? "all"}
                        onValueChange={field.onChange}
                      >
                        <SelectTrigger id="artworkType" className="bg-white gap-2">
                          <GripVertical className="h-4 w-4 text-muted-foreground flex-shrink-0" />
                          <SelectValue placeholder={t("cms.authentication.list.artworkType.filter") } />
                        </SelectTrigger>
                        <SelectContent>
                          {artworkTypeOptions?.map((opt) => (
                            <SelectItem key={opt.value} value={opt.value}>
                              {opt.label}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                    </FormControl>
                  </HeadlessFormItem>
                )}
              />
            </div>
          </div>

        </form>
      </div>
      <div>
        <DataTable
          onRowClick={handleRowClick}
          className="w-full"
          columns={columnsData}
          data={submissionListData?.data ?? []}
          options={{}}
        />
      </div>
      <div>
        <DatatablePagination
          pagination={pagination}
          setPage={setPagination}
          totalSize={submissionListData?.totalSize ?? 0}
        />
      </div>
    </FormProvider>
  );
};

